import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { FaLock } from "react-icons/fa";
import {
  fetchInvoiceInfo,
  InvoiceNotFoundError,
} from "../../api/paymentLinksApi";
import { Sidebar } from "../../components/pay/Sidebar";
import { CheckoutPanel } from "../../components/pay/CheckoutPanel";
import { ComingSoonFooter } from "../../components/pay/ComingSoonFooter";
import { QRCodeToggle } from "../../components/pay/QRCodeToggle";
import { ReceiptCard } from "../../components/pay/ReceiptCard";
import { InvoiceHeader } from "../../components/pay/invoice/InvoiceHeader";
import { InvoiceLineItems } from "../../components/pay/invoice/InvoiceLineItems";
import { InvoiceTotals } from "../../components/pay/invoice/InvoiceTotals";
import { InvoiceStatusBanner } from "../../components/pay/invoice/InvoiceStatusBanner";
import { NotFoundPage } from "./NotFoundPage";

/**
 * Public payment page for invoices at `/pay/invoice/:publicSlug`.
 *
 * CRITICAL-1 fix (P4 Codex review): the path param is the random
 * capability slug, not the enumerable `INV-001` invoice number.
 * The human invoice number still shows on the page body (so
 * payers can reference it in their records) — only the URL
 * component is the unguessable token.
 *
 * Layout mirrors PaymentPage's split shell:
 *   - Gradient sidebar (left, 40%): creator + amount summary
 *     (reuses the existing Sidebar component — we construct a
 *     synthesized "link-shaped" object from the invoice's
 *     creator + totalDisplay so the sidebar renders without
 *     branching on invoice-vs-link).
 *   - Main column (right, 60%): status banner, invoice header,
 *     line items, totals, notes, and the payment form.
 *
 * Payment form behavior:
 *   - status === 'paid'       → ReceiptCard only (no form).
 *   - status === 'cancelled'  → banner only (payment is blocked;
 *     the backend would also 410 any /confirm-direct attempt via
 *     the maxUses + status condition on the underlying link — and
 *     post-CRITICAL-2 the link is atomically disabled on cancel).
 *   - status === 'overdue' /
 *     status === 'sent'       → full payment flow (identical to
 *     PaymentPage — CheckoutPanel + QRCodeToggle + ComingSoonFooter).
 *
 * The linked payment link's /info projection comes down in the
 * same /info response as the invoice, so we don't need a second
 * HTTP roundtrip — just hand it to CheckoutPanel.
 */
export function InvoicePage() {
  const { publicSlug } = useParams();
  const [state, setState] = useState({ status: "loading" });
  const cacheRef = useRef({ key: null });

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const data = await fetchInvoiceInfo(publicSlug);
      setState({ status: "ok", data });
    } catch (err) {
      if (err instanceof InvoiceNotFoundError) {
        setState({ status: "not-found", error: err });
      } else {
        setState({ status: "error", error: err });
      }
    }
  }, [publicSlug]);

  useEffect(() => {
    if (cacheRef.current.key === publicSlug) return;
    cacheRef.current.key = publicSlug;
    load();
  }, [publicSlug, load]);

  // ── Loading ──
  if (state.status === "loading") {
    return (
      <div className="pay-loading">
        <div className="pay-loading__spinner" />
        <div style={{ color: "#6e6e73", fontSize: 14 }}>
          Loading invoice…
        </div>
      </div>
    );
  }

  // ── Not found ──
  if (state.status === "not-found") {
    return (
      <NotFoundPage
        message={
          state.error?.message ||
          `This invoice doesn't exist, or the URL was typed incorrectly.`
        }
      />
    );
  }

  // ── Network / other error ──
  if (state.status === "error") {
    return (
      <div className="pay-not-found">
        <div style={{ fontSize: 48 }} aria-hidden="true">
          ⚠️
        </div>
        <h1 className="pay-not-found__title">Couldn't load this invoice</h1>
        <p className="pay-not-found__note">
          {state.error?.message ||
            "Network error. Please check your connection and try again."}
        </p>
        <button type="button" className="pay-retry-btn" onClick={load}>
          Try again
        </button>
      </div>
    );
  }

  const { invoice, paymentLink } = state.data;
  const isPaid = invoice.status === "paid";
  const isCancelled = invoice.status === "cancelled";
  const isPayable = !isPaid && !isCancelled && paymentLink;

  // Synthesize a link-shaped object the existing Sidebar renders
  // verbatim — spares us from branching "invoice vs link" inside
  // the shared components.
  const sidebarLink = {
    linkId: paymentLink?.linkId || invoice.invoiceNumber,
    chainId: paymentLink?.chainId,
    payTo: invoice.creator.address,
    ownerUsername: invoice.creator.username,
    amountDisplay: invoice.totalDisplay,
    amountUsd: null,
    description: `Invoice ${invoice.invoiceNumber}`,
    creator: invoice.creator,
  };

  return (
    <div className="pay-shell">
      <Sidebar link={sidebarLink} />
      <main className="pay-main">
        <InvoiceStatusBanner invoice={invoice} />
        <InvoiceHeader invoice={invoice} />
        <InvoiceLineItems lineItems={invoice.lineItems} />
        <InvoiceTotals
          subtotalDisplay={invoice.subtotalDisplay}
          taxDisplay={invoice.taxDisplay}
          discountDisplay={invoice.discountDisplay}
          totalDisplay={invoice.totalDisplay}
        />
        {invoice.notes && (
          <div className="pay-invoice-notes">
            <h4 className="pay-invoice-notes__title">Notes</h4>
            <p>{invoice.notes}</p>
          </div>
        )}

        {/* Paid: receipt card replaces the payment form entirely.
            LOW-1 fix (P4 Codex review): use the concrete
            `invoice.paymentMethod` emitted by the backend — the
            old `paymentTxHash ? 'direct' : 'x402'` heuristic was
            wrong because x402 receipts ALSO carry a tx hash
            (the facilitator's settlement tx). Fall back to 'x402'
            only when the backend couldn't resolve a method
            (pre-fix receipts where the field was absent). */}
        {isPaid && (
          <ReceiptCard
            receipt={{
              receipt: {
                txHash: invoice.paymentTxHash,
                paid_by: null,
                timestamp: invoice.paidAt,
                amountDisplay: invoice.totalDisplay,
                method: invoice.paymentMethod || "x402",
              },
            }}
            link={{
              amountDisplay: invoice.totalDisplay,
              description: `Invoice ${invoice.invoiceNumber}`,
              chainId: paymentLink?.chainId,
              payTo: invoice.creator.address,
              creator: invoice.creator,
            }}
            method={invoice.paymentMethod || "x402"}
          />
        )}

        {/* Payable invoices get the full payment form. Cancelled
            invoices render the banner above and no form — the
            payment link's conditional guards would reject anyway. */}
        {isPayable && (
          <>
            <CheckoutPanel link={paymentLink} />
            <div style={{ marginTop: 24 }}>
              <QRCodeToggle />
            </div>
            <ComingSoonFooter />
          </>
        )}

        <div className="pay-security">
          <FaLock size={14} />
          {isPaid
            ? "Receipt verified on-chain. Hold on to this page for your records."
            : "You sign an authorization; horuswallet never sees your private key."}
        </div>
      </main>
    </div>
  );
}
